import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import api, { apiErrorMessage } from '@/lib/api';
import { CheckCircle2, Loader2, XCircle, AlertTriangle, ArrowRight, Home } from 'lucide-react';

const money = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0);

export default function PaymentSuccess() {
    const [sp] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = sp.get('session_id');
    const [status, setStatus] = useState('polling'); // polling | paid | expired | failed | error
    const [tx, setTx] = useState(null);
    const [attempts, setAttempts] = useState(0);
    const [err, setErr] = useState(null);

    useEffect(() => {
        if (!sessionId) { setStatus('error'); setErr('Falta session_id'); return; }
        let cancelled = false;
        const maxAttempts = 8;
        const poll = async (n) => {
            if (cancelled) return;
            setAttempts(n);
            try {
                const r = await api.get(`/payments/status/${sessionId}`);
                setTx(r.data);
                if (r.data.payment_status === 'paid') { setStatus('paid'); return; }
                if (r.data.status === 'expired') { setStatus('expired'); return; }
                if (n >= maxAttempts) { setStatus('failed'); return; }
                setTimeout(() => poll(n + 1), 2000);
            } catch (e) {
                if (n >= maxAttempts) {
                    setStatus('error');
                    setErr(apiErrorMessage(e, 'Error al verificar el pago'));
                    return;
                }
                setTimeout(() => poll(n + 1), 2500);
            }
        };
        poll(1);
        return () => { cancelled = true; };
    }, [sessionId]);

    const isProvider = tx?.type?.includes('provider'); // reserved
    const dashboardTo = isProvider ? '/provider' : '/client';

    return (
        <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--bg)' }}>
            <div className="max-w-lg w-full border-2 p-8" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}
                data-testid="payment-success-page">
                {status === 'polling' && (
                    <div className="text-center">
                        <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: '#635BFF' }} />
                        <h1 className="font-display font-black text-2xl mb-2">Verificando tu pago...</h1>
                        <p className="text-sm opacity-70">
                            Intento {attempts} de 8. Esto puede tomar unos segundos.
                        </p>
                    </div>
                )}
                {status === 'paid' && tx && (
                    <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center"
                            style={{ background: 'rgba(0,135,90,0.12)' }}>
                            <CheckCircle2 className="w-10 h-10" style={{ color: '#00875A' }} />
                        </div>
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-1">
                            Pago confirmado
                        </div>
                        <h1 className="font-display font-black text-3xl mb-2">¡Gracias!</h1>
                        <p className="text-sm opacity-80 mb-6">
                            Se procesó tu pago de <b>{money(tx.amount_mxn)}</b> correctamente.
                        </p>
                        <button onClick={() => navigate(dashboardTo)}
                            data-testid="back-to-dashboard-btn"
                            className="w-full text-white py-3 font-bold inline-flex items-center justify-center gap-2"
                            style={{ background: '#00875A' }}>
                            <Home className="w-4 h-4" /> Volver al panel
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                )}
                {(status === 'failed' || status === 'expired' || status === 'error') && (
                    <div className="text-center">
                        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center"
                            style={{ background: 'rgba(239,68,68,0.12)' }}>
                            {status === 'expired'
                                ? <AlertTriangle className="w-10 h-10" style={{ color: '#F59E0B' }} />
                                : <XCircle className="w-10 h-10" style={{ color: '#EF4444' }} />}
                        </div>
                        <h1 className="font-display font-black text-2xl mb-2">
                            {status === 'expired' ? 'Sesión expirada' : 'Pago pendiente o fallido'}
                        </h1>
                        <p className="text-sm opacity-70 mb-4">
                            {err || 'No pudimos confirmar tu pago en este momento. Si el cargo aparece en tu tarjeta, revisa tu correo — te contactaremos para reconciliar.'}
                        </p>
                        <Link to={dashboardTo}
                            className="inline-flex items-center gap-2 border-2 px-6 py-2 font-bold text-sm"
                            style={{ borderColor: 'var(--border)' }}>
                            <Home className="w-4 h-4" /> Volver al panel
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
